import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import tankCleaningService from '../../services/TankCleaningService.js';
import tankCleaningFileCabinetService from '../../services/TankCleaningFileCabinetService.js';
import userService from '../../services/UserService.js';
import { ClipboardCheck, Plus, Filter, Download, Calendar, Eye, CheckCircle, XCircle, Pencil, Trash, Mail, Copy } from "lucide-react";
import { hasPermission, ROLES } from '../../utils/permissions.js';
import { getListActionButtons, isPaidRecord } from '../../utils/roleBasedUIHelpers.js';

const TankCleaningList = () => {
  const [cleanings, setCleanings] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [clientFilter, setClientFilter] = useState('');
  const [publishedFilter, setPublishedFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [locations, setLocations] = useState([]);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedCleanings, setSelectedCleanings] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selectAllPages, setSelectAllPages] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [remarks2Modal, setRemarks2Modal] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const perPage = 10;

  // Role-based permission checks
  const userRole = user?.role_name || user?.role;
  const isClient = userRole === ROLES.CLIENT;
  const isAccountant = userRole === ROLES.ACCOUNTANT;
  const isViewOnlyRole = isClient || isAccountant;
  
  // Permission checks for tank cleaning module
  const canCreate = hasPermission(userRole, 'tankCleaning', 'create');
  const canEdit = hasPermission(userRole, 'tankCleaning', 'edit');
  const canDelete = hasPermission(userRole, 'tankCleaning', 'delete');
  const canPublish = hasPermission(userRole, 'tankCleaning', 'publish');
  const canSendEmail = hasPermission(userRole, 'tankCleaning', 'sendEmail');
  const canDownloadFileCabinet = hasPermission(userRole, 'tankCleaning', 'downloadFileCabinet');
  
  // Get action buttons based on role
  const actionButtons = getListActionButtons(userRole);

  // Check if all selected cleanings are published (for bulk PDF download)
  const allSelectedPublished = selectedCleanings.length > 0 && 
   (!isClient || selectAllPages || selectedCleanings.every(id => {
     const cleaning = cleanings.find(c => c.id === id);
     return !cleaning || cleaning.published; // if not on current page, assume it's fine
   }));

  useEffect(() => {
    if (!isClient) {
      fetchClients();
    }
    fetchLocations();
  }, [isClient]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchCleanings();
    }, 300);

    return () => clearTimeout(timer);
  }, [search, clientFilter, publishedFilter, locationFilter, paymentStatusFilter, fromDate, toDate, isClient]);

  useEffect(() => {
    fetchCleanings();
  }, [currentPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update selectAll state based on current page cleanings
  useEffect(() => {
    if (cleanings.length === 0) return;
    const allCurrentPageSelected = cleanings.every(c => selectedCleanings.includes(c.id));
    setSelectAll(allCurrentPageSelected);
  }, [cleanings, selectedCleanings]);

  const fetchClients = async () => {
    try {
      const response = await userService.getClients(null, null, { status: 'active' });
      setClients(response.data || []);
    } catch (err) {
      console.error('Failed to load clients:', err);
    }
  };

  const getPaymentStatusStyle = (paymentStatus) => {
    switch(paymentStatus) {
      case 'paid':
        return 'bg-green-50 border-green-300 text-green-700';
      case 'unpaid':
        return 'bg-red-50 border-red-300 text-red-700';
      case 'pending':
      default:
        return 'bg-yellow-50 border-yellow-300 text-yellow-700';
    }
  };

  const fetchLocations = async () => {
    try {
      const locationService = (await import('../../services/LocationService.js')).default;
      const response = await locationService.getAllLocations();
      setLocations(response.data || []);
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  };

  const fetchCleanings = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (search) filters.search = search;
      if (clientFilter) filters.client_id = clientFilter;
      if (publishedFilter) filters.published = publishedFilter;
      if (locationFilter) filters.location_id = locationFilter;
      if (fromDate) filters.from_date = fromDate;
      if (toDate) filters.to_date = toDate;
      if (paymentStatusFilter) filters.payment_status = paymentStatusFilter;

      let response;
      if (isClient) {
        // Clients only see their published cleanings
        const clientId = user?.client_id || user?.user_id;
        if (!clientId) {
          Alert.error('Client ID not found. Please login again.');
          setLoading(false);
          return;
        }
        response = await tankCleaningService.getClientCleanings(
          clientId,
          currentPage,
          perPage,
          filters
        );
      } else {
        // Internal users see all cleanings
        response = await tankCleaningService.getAll(currentPage, perPage, filters);
      }
      
      setCleanings(response.data);
      setTotalPages(response.pagination.pages);
      setTotalCount(response.pagination.total || 0);
    } catch (err) {
      Alert.error('Failed to load tank cleanings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = async () => {
    // If already all-pages selected, clear everything
    if (selectAllPages) {
      setSelectedCleanings([]);
      setSelectAll(false);
      setSelectAllPages(false);
      return;
    }

    // If only one page, just select current page
    if (totalPages <= 1) {
      setSelectedCleanings(cleanings.map(c => c.id));
      setSelectAll(true);
      return;
    }

    // Multiple pages: fetch ALL record IDs across all pages
    try {
      setSelectAllLoading(true);
      const filters = {};
      if (search) filters.search = search;
      if (clientFilter) filters.client_id = clientFilter;
      if (publishedFilter) filters.published = publishedFilter;
      if (locationFilter) filters.location_id = locationFilter;
      if (fromDate) filters.from_date = fromDate;
      if (toDate) filters.to_date = toDate;
      if (paymentStatusFilter) filters.payment_status = paymentStatusFilter;

      let response;
      if (isClient) {
        const clientId = user?.client_id || user?.user_id;
        response = await tankCleaningService.getClientCleanings(clientId, 1, totalCount || 9999, filters);
      } else {
        response = await tankCleaningService.getAllCleanings(filters);
      }
      const allIds = (response.data || []).map(c => c.id);
      setSelectedCleanings(allIds);
      setSelectAllPages(true);
      setSelectAll(true);
    } catch (err) {
      Alert.error('Failed to select all records');
      console.error(err);
    } finally {
      setSelectAllLoading(false);
    }
  };

  const handleSelectCleaning = (cleaningId) => {
    if (selectedCleanings.includes(cleaningId)) {
      const newSelected = selectedCleanings.filter(id => id !== cleaningId);
      setSelectedCleanings(newSelected);
      setSelectAllPages(false);
      const allCurrentPageSelected = cleanings.every(c => newSelected.includes(c.id));
      setSelectAll(allCurrentPageSelected);
    } else {
      const newSelected = [...selectedCleanings, cleaningId];
      setSelectedCleanings(newSelected);
      const allCurrentPageSelected = cleanings.every(c => newSelected.includes(c.id));
      setSelectAll(allCurrentPageSelected);
    }
  };

  const handleDownloadCSV = async () => {
    if (selectedCleanings.length === 0) {
      Alert.warning('Please select at least one cleaning record to download');
      return;
    }

    try {
      const blob = await tankCleaningService.exportCSV(selectedCleanings);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `tank_cleanings_${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      Alert.success(`${selectedCleanings.length} cleaning(s) exported successfully!`);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to export CSV');
      console.error(err);
    }
  };

  const handleSendBulkEmail = async () => {
    if (selectedCleanings.length === 0) {
      Alert.warning('Please select at least one cleaning record to send email');
      return;
    }

    const confirmed = await Alert.confirm(
      `Are you sure you want to send email for ${selectedCleanings.length} selected cleaning(s)?`,
      'Send Bulk Email'
    );

    if (confirmed) {
      try {
        await tankCleaningService.sendBulkEmail(selectedCleanings);
        Alert.success(`Email processing started for ${selectedCleanings.length} cleaning(s)!`);
        setSelectedCleanings([]);
        setSelectAll(false);
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to send bulk email');
      }
    }
  };

  const handleDownloadBulkPDF = async () => {
    if (selectedCleanings.length === 0) {
      Alert.warning('Please select at least one cleaning record to download');
      return;
    }

    try {
      const response = await tankCleaningService.downloadBulkPDF(selectedCleanings);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const filename = disposition?.split('filename=')[1]?.replace(/"/g, '')
        || `tank_cleanings_${new Date().toISOString().split('T')[0]}.zip`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      Alert.success(`${selectedCleanings.length} cleaning(s) downloaded successfully!`);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to download PDFs');
      console.error(err);
    }
  };

  const handleDownloadAllCabinet = async () => {
    try {
      const blob = await tankCleaningFileCabinetService.downloadAllCabinet();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `tank_cleaning_file_cabinet_${timestamp}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      Alert.success('File cabinet downloaded successfully!');
    } catch (err) {
      Alert.error('Failed to download file cabinet');
      console.error(err);
    }
  };

  const handleTogglePublished = async (cleaningId, currentStatus) => {
    try {
      await tankCleaningService.togglePublished(cleaningId);
      Alert.success(`Tank cleaning ${currentStatus ? 'unpublished' : 'published'} successfully!`);
      fetchCleanings();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update published status');
    }
  };

  const handleDelete = async (cleaningId, cleaningNo) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete cleaning record "${cleaningNo}"? This action cannot be undone.`,
      'Delete Tank Cleaning'
    );
    
    if (confirmed) {
      try {
        await tankCleaningService.delete(cleaningId);
        Alert.success('Tank cleaning deleted successfully!');
        
        if (cleanings.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchCleanings();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete tank cleaning');
      }
    }
  };

  const handleDuplicate = async (cleaningId, cleaningNo) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to duplicate tank cleaning record "${cleaningNo}"? A new record will be created with the same data.`,
      'Duplicate Tank Cleaning'
    );
    if (confirmed) {
      try {
        await tankCleaningService.duplicate(cleaningId);
        Alert.success('Tank cleaning duplicated successfully!');
        fetchCleanings();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to duplicate tank cleaning');
      }
    }
  };

  const handleDownloadCertificate = async (cleaningId, cleaningNo, tankNo) => {
    try {
      const blob = await tankCleaningService.generateCertificate(cleaningId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cleaning_certificate_${(tankNo || cleaningNo).replace(/\//g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      Alert.success('Certificate downloaded successfully!');
    } catch (err) {
      Alert.error('Failed to download certificate');
    }
  };

  const clearFilters = () => {
    setSearch('');
    setClientFilter('');
    setPublishedFilter('');
    setLocationFilter('');
    setPaymentStatusFilter('');
    setFromDate('');
    setToDate('');
    setCurrentPage(1);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.client_id === clientId);
    return client ? client.name : clientId;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <>
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              {/* Header */}
              <div className="max-w-8xl mx-auto">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <ClipboardCheck className="w-5 h-5 text-buttonBg" />
                      Tank Cleanings
                    </h1>
                  </div>

                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {selectedCleanings.length > 0 && (
                      <>
                        <button
                          onClick={handleDownloadCSV}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                        >
                          <Download size={18} />
                          <span className="hidden sm:inline">Download CSV ({selectedCleanings.length})</span>
                          <span className="sm:hidden">CSV ({selectedCleanings.length})</span>
                        </button>
                        {canSendEmail && (
                          <button
                            onClick={handleSendBulkEmail}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                          >
                            <Mail size={18} />
                            <span className="hidden sm:inline">Send Email ({selectedCleanings.length})</span>
                            <span className="sm:hidden">Email ({selectedCleanings.length})</span>
                          </button>
                        )}
                        <button
                          onClick={allSelectedPublished ? handleDownloadBulkPDF : undefined}
                          disabled={!allSelectedPublished}
                          title={!allSelectedPublished ? 'Only published records can be downloaded as PDF' : ''}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                            allSelectedPublished 
                              ? 'bg-purple-600 text-white hover:bg-purple-700 cursor-pointer' 
                              : 'bg-purple-300 text-white cursor-not-allowed opacity-60'
                          }`}
                        >
                          <Download size={18} />
                          <span className="hidden sm:inline">Download Reports ({selectedCleanings.length})</span>
                          <span className="sm:hidden">Reports ({selectedCleanings.length})</span>
                        </button>
                      </>
                    )}
                    {canDownloadFileCabinet && (
                      <button
                        onClick={handleDownloadAllCabinet}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                      >
                        <Download size={18} />
                        <span className="hidden sm:inline">All Files</span>
                      </button>
                    )}
                    {canCreate && (
                      <button
                        onClick={() => navigate('/tank-cleanings/add')}
                        className="
                          relative overflow-hidden
                          group flex items-center justify-center gap-2
                          px-3 sm:px-3 py-2.5 sm:py-2
                          text-xs sm:text-sm
                          rounded-lg
                          border border-buttonBg
                          bg-buttonBg text-buttonTxt
                          font-medium whitespace-nowrap

                          transition-colors duration-300 ease-out
                          hover:bg-transparent hover:text-buttonBg
                        "
                      >
                        {/* COLLAPSING BACKGROUND */}
                        <span
                          className="
                            absolute inset-0
                            bg-buttonBg
                            scale-x-100
                            group-hover:scale-x-0
                            origin-center
                            transition-transform duration-300 ease-out
                            z-0
                          "
                        />

                        {/* ICON */}
                        <Plus
                          className="
                            w-4 h-4 relative z-10
                            transition-transform duration-300 ease-out
                            group-hover:scale-90
                          "
                        />

                        <span className="relative z-10">New Cleaning</span>
                        <span className="sm:hidden">New</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Filters */}
              <div className="bg-white p-4 sm:p-6 rounded-sm shadow-sm mb-4 sm:mb-6 overflow-visible">
                <div className="flex items-center justify-between mb-0 sm:mb-4">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 sm:w-4 sm:h-4 text-buttonBg font-bold"/>
                    <h3 className="text-base sm:text-lg font-semibold text-tertiary">Filters</h3>
                  </div>
                </div>
                <div className='hidden sm:block'>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end overflow-visible">
                    {/* Search */}
                    <div className="flex items-center gap-2 lg:col-span-1">
                      <input
                        type="text"
                        placeholder="Search by Cleaning ID, Tank No..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full h-[42px] px-4 border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 shadow-sm"
                      />
                    </div>

                    {/* Mobile Filter Toggle */}
                    <button
                      onClick={() => setShowMobileFilters(!showMobileFilters)}
                      className="lg:hidden flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <Filter size={18} />
                      Filters
                    </button>

                    {/* Desktop Filters */}
                    {!isClient && (
                    <div className="hidden lg:flex gap-3 flex-wrap relative z-20">
                      
                        <select
                          value={clientFilter}
                          onChange={(e) => setClientFilter(e.target.value)}
                          className="px-4 py-2 w-full border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 relative z-20"
                          >

                          <option value="">All Clients</option>
                          {clients.map((client) => (
                            <option key={client.client_id} value={client.client_id}>
                              {client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim()}
                            </option>
                          ))}
                        </select>
                      
                    </div>
                    )}
                    <div className="hidden lg:flex gap-3 flex-wrap">
                      <select
                        value={publishedFilter}
                        onChange={(e) => setPublishedFilter(e.target.value)}
                        className="px-4 py-2 w-full border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value="">All Status</option>
                        <option value="true">Published</option>
                        <option value="false">Unpublished</option>
                      </select>
                    </div>

                    <div className="hidden lg:flex gap-3 flex-wrap">
                      <select
                        value={locationFilter}
                        onChange={(e) => setLocationFilter(e.target.value)}
                        className="px-4 py-2 w-full border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value="">All Locations</option>
                        {locations.map((loc) => (
                          <option key={loc.id} value={loc.id}>
                            {loc.city}, {loc.state}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="hidden lg:flex gap-3 flex-wrap">
                      <select
                        value={paymentStatusFilter}
                        onChange={(e) => setPaymentStatusFilter(e.target.value)}
                        className="px-4 py-2 w-full border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value="">All Payment</option>
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="unpaid">Unpaid</option>
                      </select>
                    </div>
                    <div className="flex gap-3 flex-wrap">
                      <div className='relative w-full'>
                        <input
                          type="date"
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                          placeholder="From Date"
                          className="px-4 py-2 w-full border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                        <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                      </div>
                    </div>
                      
                    <div className="flex gap-3 flex-wrap">
                      <div className='relative w-full'>
                        <input
                          type="date"
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                          placeholder="To Date"
                          className="px-4 py-2 w-full border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                        <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                      </div>

                      {(search || clientFilter || publishedFilter || locationFilter || paymentStatusFilter || fromDate || toDate) && (
                        <button
                          onClick={clearFilters}
                          className="px-4 py-2 text-gray-600 hover:text-gray-800 underline text-sm"
                        >
                          Clear Filters
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Mobile Filters Dropdown */}
                {showMobileFilters && (
                  <div className="lg:hidden mt-4 space-y-3 p-4 bg-gray-50 rounded-sm">
                    {!isClient && (
                      <select
                        value={clientFilter}
                        onChange={(e) => setClientFilter(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value="">All Clients</option>
                        {clients.map((client) => (
                          <option key={client.client_id} value={client.client_id}>
                            {client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim()}
                          </option>
                        ))}
                      </select>
                    )}

                    <select
                      value={publishedFilter}
                      onChange={(e) => setPublishedFilter(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">All Status</option>
                      <option value="true">Published</option>
                      <option value="false">Unpublished</option>
                    </select>

                    <select
                      value={locationFilter}
                      onChange={(e) => setLocationFilter(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">All Locations</option>
                      {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.city}, {loc.state}
                        </option>
                      ))}
                    </select>

                    <select
                      value={paymentStatusFilter}
                      onChange={(e) => setPaymentStatusFilter(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">All Payment</option>
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="unpaid">Unpaid</option>
                    </select>

                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      placeholder="From Date"
                      className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />

                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      placeholder="To Date"
                      className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />

                    {(search || clientFilter || publishedFilter || locationFilter || paymentStatusFilter || fromDate || toDate) && (
                      <button
                        onClick={clearFilters}
                        className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 underline text-sm"
                      >
                        Clear Filters
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="bg-white rounded-sm shadow-sm">
                <div className="p-4 sm:p-6 lg:p-0">
                {/* Table View - Desktop */}
                  <div className="hidden lg:block bg-white rounded-lg shadow-sm">
                    {loading ? (
                      <div className="p-12 text-center text-gray-600">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                        Loading tank cleanings...
                      </div>
                    ) : cleanings.length === 0 ? (
                      <div className="p-12 text-center text-gray-600">No tank cleanings found</div>
                    ) : (
                      <>
                        <div className="hidden md:block w-full">
                                  <table className="w-full table-fixed border-collapse">
                                    <thead>
                                      <tr className="bg-primary text-tertiary">
                                <th className="px-4 py-4 w-[4%] text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    <input
                                      type="checkbox"
                                      checked={selectAll || selectAllPages}
                                      onChange={handleSelectAll}
                                      disabled={selectAllLoading}
                                      className={`w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 ${selectAllLoading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                                    />
                                </th>
                                <th className="px-4 py-4 w-[13%] text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Cleaning ID
                                </th>
                                  <th className="px-4 py-4 w-[12%] text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Client
                                </th>
                                <th className="px-4 py-4 w-[9%] text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Tank No
                                </th>
                                <th className="px-4 py-4 w-[15%] text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Remarks 2
                                </th>
                                <th className="px-4 py-4 w-[12%] text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Survey Date
                                </th>
                                <th className="px-6 py-4 w-[8%] text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Depot Name
                                </th>
                                <th className="px-6 py-4 w-[13%] text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Previous Cargo
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Published
                                </th>
                                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {selectAllLoading && (
                                <tr>
                                  <td colSpan="10" className="px-4 py-2 bg-orange-50 text-center text-sm text-orange-800 border-b border-orange-200">
                                    <span className="inline-flex items-center gap-2">
                                      <svg className="animate-spin h-4 w-4 text-orange-600" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                                      </svg>
                                      Selecting all {totalCount} records...
                                    </span>
                                  </td>
                                </tr>
                              )}
                              {selectAllPages && !selectAllLoading && (
                                <tr>
                                  <td colSpan="10" className="px-4 py-2 bg-orange-50 text-center text-sm text-orange-800 border-b border-orange-200">
                                    All {selectedCleanings.length} records across all pages are selected.{' '}
                                    <button
                                      onClick={() => { setSelectedCleanings([]); setSelectAll(false); setSelectAllPages(false); }}
                                      className="font-semibold underline hover:text-orange-600"
                                    >
                                      Clear selection
                                    </button>
                                  </td>
                                </tr>
                              )}
                              {cleanings.map((cleaning) => (
                                <tr 
                                  key={cleaning.id} 
                                  className={`hover:bg-gray-50 ${
                                    !isClient 
                                      ? cleaning.published 
                                        ? 'bg-white' 
                                        : 'bg-white'
                                      : ''
                                  }`}
                                >
                                  <td className="px-6 py-4 whitespace-nowrap">
                                      <input
                                        type="checkbox"
                                        checked={selectedCleanings.includes(cleaning.id)}
                                        onChange={() => handleSelectCleaning(cleaning.id)}
                                        className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
                                      />
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div
                                      className={`inline-block px-3 py-1.5 rounded-md border-2 text-sm font-semibold cursor-pointer hover:opacity-80 transition-opacity ${getPaymentStatusStyle(cleaning.payment_status)}`}
                                      onClick={() => navigate(`/tank-cleanings/view/${cleaning.id}`)}
                                    >
                                      {cleaning.cleaning_id}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                      <div className="text-sm text-gray-900">{getClientName(cleaning.client_id)}</div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="text-sm text-left text-gray-900">{cleaning.tank_no || 'N/A'}</div>
                                  </td>
                                  <td className="px-6 py-4">
                                    {cleaning.remarks_2 ? (
                                      <button
                                        onClick={() => setRemarks2Modal({ text: cleaning.remarks_2, cleaningId: cleaning.cleaning_id })}
                                        className="text-sm text-left text-gray-900 max-w-[160px] truncate block hover:text-orange-600 hover:underline cursor-pointer transition-colors"
                                      >
                                        {cleaning.remarks_2}
                                      </button>
                                    ) : (
                                      <span className="text-sm text-gray-400">N/A</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">{formatDate(cleaning.date_of_survey)}</div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="text-sm text-left text-gray-900">{cleaning.depot_name || 'N/A'}</div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="text-sm text-left text-gray-900 max-w-xs truncate">
                                      {cleaning.previous_cargo || 'N/A'}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                      {cleaning.published ? (
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                          Published
                                        </span>
                                      ) : (
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                          Unpublished
                                        </span>
                                      )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium relative">
                                        <button
                                          onClick={() => setActiveDropdown(activeDropdown === cleaning.id ? null : cleaning.id)}
                                          className="text-gray-400 hover:text-gray-600"
                                        >
                                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                          </svg>
                                        </button>
                                        
                                        {activeDropdown === cleaning.id && (
                                          <div
                                            ref={dropdownRef}
                                            className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10"
                                          >
                                            {actionButtons.view && (
                                              <button
                                                onClick={() => {
                                                  navigate(`/tank-cleanings/view/${cleaning.id}`);
                                                  setActiveDropdown(null);
                                                }}
                                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center"
                                              >
                                                <Eye size={16} className="text-buttonBg" />
                                                <span className='text-base capitalize'>View Details</span>
                                              </button>
                                            )}
                                            {actionButtons.downloadCertificate && (
                                              <button
                                                onClick={() => {
                                                  handleDownloadCertificate(cleaning.id, cleaning.cleaning_id, cleaning.tank_no);
                                                  setActiveDropdown(null);
                                                }}
                                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center"
                                              >
                                                <Download size={16} className="text-buttonBg" />
                                                <span className='text-base capitalize'>Download Report</span>
                                              </button>
                                            )}
                                            {actionButtons.publish && (
                                              <button
                                                onClick={() => {
                                                  handleTogglePublished(cleaning.id, cleaning.published);
                                                  setActiveDropdown(null);
                                                }}
                                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center"
                                              >
                                                {cleaning.published ? (
                                                  <XCircle size={16} className="text-buttonBg" />
                                                ) : (
                                                  <CheckCircle size={16} className="text-buttonBg" />
                                                )}
                                                <span className='text-base capitalize'>
                                                  {cleaning.published ? 'Unpublish' : 'Publish'}
                                                </span>
                                              </button>
                                            )}
                                            {actionButtons.edit && !isPaidRecord(cleaning.payment_status) && (
                                              <button
                                                onClick={() => {
                                                  navigate(`/tank-cleanings/edit/${cleaning.id}`);
                                                  setActiveDropdown(null);
                                                }}
                                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center"
                                              >
                                                <Pencil size={16} className="text-buttonBg" />
                                                <span className='text-base capitalize'>Edit</span>
                                              </button>
                                            )}
                                            {actionButtons.delete && !isPaidRecord(cleaning.payment_status) && (
                                              <button
                                                onClick={() => {
                                                  handleDelete(cleaning.id, cleaning.cleaning_id);
                                                  setActiveDropdown(null);
                                                }}
                                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center"
                                              >
                                                <Trash size={16} className="text-buttonBg" />
                                                <span className='text-base capitalize'>Delete</span>
                                              </button>
                                            )}
                                            {!isViewOnlyRole && (
                                              <button
                                                onClick={() => {
                                                  handleDuplicate(cleaning.id, cleaning.cleaning_id);
                                                  setActiveDropdown(null);
                                                }}
                                                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex gap-2 items-center"
                                              >
                                                <Copy size={16} className="text-buttonBg" />
                                                <span className='text-base capitalize'>Duplicate</span>
                                              </button>
                                            )}
                                          </div>
                                        )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination */}
                        <div className="hidden md:flex bg-gray-50 px-6 py-4 justify-between items-center border-t">
                          <div className="text-sm text-gray-700">
                            Showing {cleanings.length} cleaning{cleanings.length !== 1 ? 's' : ''}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                              disabled={currentPage === 1}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                              ← Previous
                            </button>
                            <span className="px-4 py-2 text-sm text-gray-700">
                              Page {currentPage} of {totalPages}
                            </span>
                            <button
                              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                              disabled={currentPage === totalPages}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                              Next →
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  {/* Card View - Mobile/Tablet */}
                  <div className="lg:hidden space-y-3 sm:space-y-4">
                    {loading ? (
                      <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                        Loading tank cleanings...
                      </div>
                    ) : cleanings.length === 0 ? (
                      <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">No tank cleanings found</div>
                    ) : (
                      <>
                        {cleanings.map((cleaning) => (
                          <div 
                            key={cleaning.id} 
                            className={`rounded-lg shadow-sm p-4 sm:p-5 ${
                              !isClient 
                                ? cleaning.published 
                                  ? 'bg-green-50' 
                                  : 'bg-red-50'
                                : 'bg-white'
                            }`}
                          >
                            <div className="flex items-start gap-3 sm:gap-4 mb-3">
                              {!isClient && (
                                <input
                                  type="checkbox"
                                  checked={selectedCleanings.includes(cleaning.id)}
                                  onChange={() => handleSelectCleaning(cleaning.id)}
                                  className="mt-1 w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
                                />
                              )}
                              <ClipboardCheck className="w-10 h-10 text-tertiary flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-base sm:text-lg">
                                  <span
                                    className={`inline-block px-3 py-1.5 rounded-md border-2 text-sm font-semibold cursor-pointer hover:opacity-80 transition-opacity ${getPaymentStatusStyle(cleaning.payment_status)}`}
                                    onClick={() => navigate(`/tank-cleanings/view/${cleaning.id}`)}
                                  >
                                    {cleaning.cleaning_id}
                                  </span>
                                </h3>
                                <p className="text-xs sm:text-sm text-gray-600 truncate mt-1">
                                  {!isClient && getClientName(cleaning.client_id)}
                                </p>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm mb-3">
                              <div>
                                <span className="text-gray-500">Tank No:</span>
                                <p className="text-gray-900 font-medium">{cleaning.tank_no || 'N/A'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">Remarks 2:</span>
                                {cleaning.remarks_2 ? (
                                  <button
                                    onClick={() => setRemarks2Modal({ text: cleaning.remarks_2, cleaningId: cleaning.cleaning_id })}
                                    className="text-gray-900 font-medium truncate block w-full text-left hover:text-orange-600 hover:underline cursor-pointer transition-colors"
                                  >
                                    {cleaning.remarks_2}
                                  </button>
                                ) : (
                                  <p className="text-gray-400 font-medium">N/A</p>
                                )}
                              </div>
                              <div>
                                <span className="text-gray-500">Survey Date:</span>
                                <p className="text-gray-900 font-medium">{formatDate(cleaning.date_of_survey)}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">Published:</span>
                                <p className={`font-medium ${cleaning.published ? 'text-green-700' : 'text-red-700'}`}>
                                  {cleaning.published ? 'Yes' : 'No'}
                                </p>
                              </div>
                              <div className="col-span-2">
                                <span className="text-gray-500">Depot Name:</span>
                                <p className="text-gray-900 font-medium truncate">{cleaning.depot_name || 'N/A'}</p>
                              </div>
                            </div>

                            <div className="flex gap-2">
                              <button
                                onClick={() => navigate(`/tank-cleanings/view/${cleaning.id}`)}
                                className="flex-1 px-3 sm:px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs sm:text-sm font-medium"
                              >
                                👁️ View
                              </button>
                              <button
                                onClick={() => (!isClient || cleaning.published) && handleDownloadCertificate(cleaning.id, cleaning.cleaning_id, cleaning.tank_no)}
                                disabled={isClient && !cleaning.published}
                                className={`flex-1 px-3 sm:px-4 py-2 border rounded-lg transition-colors text-xs sm:text-sm font-medium ${isClient && !cleaning.published ? 'border-gray-200 text-gray-400 opacity-50 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                              >
                                📄 Report
                              </button>
                              {canEdit && !isPaidRecord(cleaning.payment_status) && (
                                <button
                                  onClick={() => navigate(`/tank-cleanings/edit/${cleaning.id}`)}
                                  className="px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm font-medium"
                                >
                                  ✏️ Edit
                                </button>
                              )}
                              {canDelete && !isPaidRecord(cleaning.payment_status) && (
                                <button
                                  onClick={() => handleDelete(cleaning.id, cleaning.cleaning_id)}
                                  className="px-3 sm:px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-xs sm:text-sm font-medium"
                                >
                                  🗑️ Delete
                                </button>
                              )}
                              {!isViewOnlyRole && (
                                <button
                                  onClick={() => handleDuplicate(cleaning.id, cleaning.cleaning_id)}
                                  className="px-3 sm:px-4 py-2 border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors text-xs sm:text-sm font-medium"
                                >
                                  📋 Duplicate
                                </button>
                              )}
                            </div>
                          </div>
                        ))}

                        {/* Mobile Pagination */}
                        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
                          <div className="text-xs sm:text-sm text-gray-700 text-center mb-3">
                            Showing {cleanings.length} cleaning{cleanings.length !== 1 ? 's' : ''} - Page {currentPage} of {totalPages}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                              disabled={currentPage === 1}
                              className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium"
                            >
                              ← Previous
                            </button>
                            <button
                              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                              disabled={currentPage === totalPages}
                              className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium"
                            >
                              Next →
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>

      {/* Remarks 2 Modal */}
      {remarks2Modal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
          onClick={() => setRemarks2Modal(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Remarks 2</h3>
                <p className="text-xs text-gray-400 mt-0.5">{remarks2Modal.cleaningId}</p>
              </div>
              <button
                onClick={() => setRemarks2Modal(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Body */}
            <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-800 leading-relaxed whitespace-pre-wrap max-h-60 overflow-y-auto">
              {remarks2Modal.text}
            </div>
            {/* Footer */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setRemarks2Modal(null)}
                className="px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TankCleaningList;
